// 적 모델 캐싱 시스템 - 렉 해결을 위한 최적화
// 한국어 주석: 적 모델 캐시 관리 클래스 (webpack 환경에서도 동작하도록 window.* 참조 사용)
class EnemyModelCache {
  constructor() {
    // 모델 캐시 (타입별로 저장)
    this.modelCache = new Map();

    // 로딩 상태 추적
    this.loadingPromises = new Map();

    // 캐시된 모델들
    this.cachedModels = new Map();
  }

  // 모델 사전 로딩 (게임 시작 시 호출)
  async preloadAllModels() {
    Utils.log("적 모델 사전 로딩 시작...");
    // 한국어 주석: 모듈 스코프에서 전역 상수를 직접 참조하면 ReferenceError가 발생하므로 window를 통해 접근
    const enemyTypes = Object.entries(window.EnemyTypesConfig || {})
      .filter(([_, cfg]) => cfg && typeof cfg === "object" && typeof cfg.MODEL_PATH === "string")
      .map(([type]) => type);
    const loadPromises = enemyTypes.map((type) => this.preloadModel(type));

    await Promise.all(loadPromises);

    Utils.log("모든 적 모델 사전 로딩 완료!");
  }

  // 특정 타입 모델 사전 로딩
  async preloadModel(enemyType) {
    if (this.modelCache.has(enemyType)) {
      return this.modelCache.get(enemyType);
    }

    // 이미 로딩 중이면 기다리기
    if (this.loadingPromises.has(enemyType)) {
      return await this.loadingPromises.get(enemyType);
    }

    // 한국어 주석: 전역 등록된 EnemyTypesConfig 사용
    const config = (window.EnemyTypesConfig || {})[enemyType];
    if (!config) {
      throw new Error(`알 수 없는 적 타입: ${enemyType}`);
    }

    // 모델 경로 검증
    const modelPath = config.MODEL_PATH;
    if (!modelPath || typeof modelPath !== "string") {
      throw new Error(
        `${enemyType}의 MODEL_PATH가 유효하지 않습니다: ${modelPath}`
      );
    }

    Utils.log(`${enemyType} 모델 로딩 시작: ${modelPath}`);

    const loadPromise = new Promise((resolve, reject) => {
      const loader = new THREE.GLTFLoader();

      loader.load(
        modelPath,
        (gltf) => {
          // 원본 모델 저장
          this.modelCache.set(enemyType, gltf);
          this.loadingPromises.delete(enemyType);

          Utils.log(`${enemyType} 모델 캐시 완료`);
          resolve(gltf);
        },
        (progress) => {
          // 로딩 진행률 로그 (디버깅용)
          if (progress.lengthComputable) {
            const percentComplete = (progress.loaded / progress.total) * 100;
            Utils.log(
              `${enemyType} 로딩 진행률: ${percentComplete.toFixed(1)}%`
            );
          }
        },
        (error) => {
          this.loadingPromises.delete(enemyType);
          console.error(
            `${enemyType} 모델 로드 실패 (경로: ${modelPath}):`,
            error
          );
          reject(
            new Error(`${enemyType} 모델 로드 실패: ${error.message || error}`)
          );
        }
      );
    });

    this.loadingPromises.set(enemyType, loadPromise);
    return await loadPromise;
  }

  // 캐시된 모델 복사본 생성 (인스턴싱)
  async createEnemyModel(enemyType) {
    // 캐싱 활성화 (성능 최적화)
    const USE_CACHE = true; // 캐시 사용으로 성능 향상

    if (!USE_CACHE) {
      // 캐싱 없이 매번 새로 로드 (안전하지만 느림)
      return await this.loadModelDirectly(enemyType);
    }

    // 캐시에서 모델 가져오기
    let gltf = this.modelCache.get(enemyType);

    if (!gltf) {
      // 캐시에 없으면 로드
      gltf = await this.preloadModel(enemyType);
    }

    // 완전한 모델 복사본 생성 (재귀적 복제)
    const clonedScene = this.deepCloneObject3D(gltf.scene);

    // 애니메이션도 복사
    const animations = gltf.animations;

    return {
      scene: clonedScene,
      animations: animations,
    };
  }

  // 직접 로딩 (캐싱 없이)
  async loadModelDirectly(enemyType) {
    const config = (window.EnemyTypesConfig || {})[enemyType];
    if (!config) {
      throw new Error(`알 수 없는 적 타입: ${enemyType}`);
    }

    return new Promise((resolve, reject) => {
      const loader = new THREE.GLTFLoader();

      loader.load(
        config.MODEL_PATH,
        (gltf) => {
          resolve({
            scene: gltf.scene,
            animations: gltf.animations,
          });
        },
        undefined,
        (error) => {
          console.error(`${enemyType} 직접 로드 실패:`, error);
          reject(error);
        }
      );
    });
  }

  // 3D 오브젝트의 완전한 복제 (모든 메시와 재질 포함)
  deepCloneObject3D(obj) {
    // Three.js SkeletonUtils 사용이 더 안전하지만, 없는 경우 수동 복제
    if (THREE.SkeletonUtils && THREE.SkeletonUtils.clone) {
      return THREE.SkeletonUtils.clone(obj);
    }

    // 수동 복제 방식 - 더 안전한 복제
    const cloned = obj.clone(true); // 재귀적 복제 플래그

    // 모든 메시를 순회하며 재질 복제
    cloned.traverse((child) => {
      if (child.isMesh) {
        // Geometry는 개별 복제 (안전성을 위해)
        if (child.geometry) {
          child.geometry = child.geometry.clone();
        }

        // Material은 개별 복제 (색상 변경 등을 위해)
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material = child.material.map((mat) => mat.clone());
          } else {
            child.material = child.material.clone();
          }
        }

        // 메시 속성 복사
        child.castShadow = true;
        child.receiveShadow = false;
      }

      // 스켈레톤 메시의 경우
      if (child.isSkinnedMesh) {
        // 스켈레톤은 공유하지 않고 각자 가져야 함
        if (child.skeleton) {
          child.skeleton = child.skeleton.clone();
        }
      }

      // 기타 중요한 속성들 복사
      if (child.userData) {
        child.userData = JSON.parse(JSON.stringify(child.userData));
      }
    });

    return cloned;
  }

  // 캐시 상태 확인
  getCacheStatus() {
    const enemyTypes = Object.entries(window.EnemyTypesConfig || {})
      .filter(([_, cfg]) => cfg && typeof cfg === "object" && typeof cfg.MODEL_PATH === "string")
      .map(([type]) => type);
    const cached = Array.from(this.modelCache.keys());
    const loading = Array.from(this.loadingPromises.keys());

    return {
      total: enemyTypes.length,
      cached: cached.length,
      loading: loading.length,
      cachedTypes: cached,
      loadingTypes: loading,
    };
  }

  // 메모리 정리
  clearCache() {
    this.modelCache.clear();
    this.loadingPromises.clear();
    Utils.log("적 모델 캐시가 정리되었습니다.");
  }
}

// 전역 캐시 인스턴스
window.enemyModelCache = new EnemyModelCache();

// webpack 빌드를 위해 default export 추가 및 전역 클래스 등록
export default EnemyModelCache;
window.EnemyModelCache = EnemyModelCache;
